import React, { FormEventHandler, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';

import LocalStorageService from '../../services/LocalStorageService';
import { Avatar, Box, CssBaseline, Grid, Paper, TextField, Typography, Button } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import auth from '../../services/auth';
import type { TokenPayload } from '../../services/LocalStorageService';
import GoogleSignInButton from './GoogleSignInButton';

const LOGIN_QUERY = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      access_token
      refresh_token
      user {
        name
      }
      permissions
    }
  }
`;

const LOGIN_WITH_GOOGLE = gql`
  mutation LoginWithGoogle($idToken: String!) {
    loginWithGoogle(idToken: $idToken) {
      access_token
      refresh_token
      user {
        name
      }
      permissions
    }
  }
`;

type AdminLoginResult = {
  access_token: string;
  refresh_token: string;
  user: {name: string};
  permissions: string[];
};

const LoginPage = () => {
  // const [email, setEmail] = useState<string>('');
  // const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();

  const [login] = useMutation<{ login: AdminLoginResult }>(LOGIN_QUERY);
  const [loginWithGoogle] = useMutation<{ loginWithGoogle: AdminLoginResult }>(LOGIN_WITH_GOOGLE);

  const completeAdminLogin = (result: AdminLoginResult | undefined) => {
    if (result && auth.hasAccess(result.permissions)) {
      LocalStorageService.setTokens(result as unknown as TokenPayload);
      navigate('/');
      return;
    }
    setError('You do not have access to the admin panel.');
  };

  const onSubmitHandler: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    const payload = {
      email: data.get('email'),
      password: data.get('password')
    };
    setError('');
    try {
      const result = await login({
        variables: payload
      });
      completeAdminLogin(result.data?.login);
    } catch (error) {
      if (error instanceof Error && error.message) {
        setError(error.message);
      } else {
        console.log('Network error.');
      }
    }
  };

  const onGoogleCredential = async (idToken: string) => {
    setError('');
    try {
      const result = await loginWithGoogle({
        variables: {idToken}
      });
      completeAdminLogin(result.data?.loginWithGoogle);
    } catch (error) {
      if (error instanceof Error && error.message) {
        setError(error.message);
      } else {
        setError('Network error.');
      }
    }
  };

  return (
    <Grid container component="main" sx={{ height: '100vh', p: 2, alignItems: 'center', justifyContent: 'center' }}>
      <CssBaseline />
      <Grid size={{ xs: 12, sm: 8, md: 5 }} component={Paper} elevation={6} square>
        <Box
          sx={{
            my: 8,
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <LockOutlined />
          </Avatar>
          <Typography component="h1" variant="h5">
            Sign in
          </Typography>
          <Box component="form" noValidate onSubmit={onSubmitHandler} sx={{ mt: 1 }}>
            <TextField margin="normal" required fullWidth id="email" label="Email Address" name="email" autoComplete="email" autoFocus />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
            />
            <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} size="medium">
              LogIn
            </Button>
          </Box>
          <GoogleSignInButton onCredential={onGoogleCredential} onError={setError} />
          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default LoginPage;
